import { useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';
import SEOHead from '@/components/feature/SEOHead';
import Header from '@/components/feature/Header';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import { articles, siteInfo } from '@/mocks/articles';
import { categories } from '@/mocks/categories';

export default function Article() {
  const { id } = useParams<{ id: string }>();
  const article = articles.find((a) => a.id === Number(id));

  const relatedArticles = article
    ? articles
        .filter((a) => a.category === article.category && a.id !== article.id)
        .slice(0, 3)
    : [];

  const categoryInfo = article
    ? categories.find((c) => c.slug === article.category)
    : undefined;

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [id]);

  if (!article) {
    return (
      <>
        <SEOHead
          title="文章未找到"
          description="抱歉，您访问的文章不存在或已被移除。请浏览其他精彩内容。"
          keywords="51网址导航, 网址导航"
          canonicalUrl="/"
        />
        <div className="min-h-screen bg-background-50">
          <Header />
          <main className="pt-20 pb-10 flex items-center justify-center min-h-[60vh]">
            <div className="text-center">
              <div className="w-20 h-20 flex items-center justify-center bg-background-200 rounded-full mx-auto mb-5">
                <i className="ri-file-unknow-line text-3xl text-foreground-400"></i>
              </div>
              <h1 className="text-xl font-bold text-foreground-950 mb-2 font-heading">文章未找到</h1>
              <p className="text-foreground-500 text-sm mb-6">您访问的文章不存在或已被移除</p>
              <Link
                to="/"
                className="inline-flex px-6 py-2.5 bg-primary-500 text-white rounded-full text-sm font-medium hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
              >
                返回首页
              </Link>
            </div>
          </main>
          <Footer />
        </div>
      </>
    );
  }

  const articleSchema = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: article.title,
    description: article.summary,
    author: {
      '@type': 'Person',
      name: article.author,
    },
    datePublished: article.date,
    dateModified: article.date,
    image: article.image,
    publisher: {
      '@type': 'Organization',
      name: siteInfo.name,
    },
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': `https://vfufj.cn/article/${article.id}`,
    },
  };

  const breadcrumbItems = [
    { label: '首页', href: '/' },
    { label: categoryInfo?.name || '分类', href: `/category/${article.category}` },
    { label: article.title },
  ];

  return (
    <>
      <SEOHead
        title={article.title}
        description={article.summary}
        keywords={article.tags.join(', ') + ', 51网址导航, 网址导航'}
        canonicalUrl={`/article/${article.id}`}
        ogType="article"
        ogImage={article.image}
        structuredData={articleSchema}
        articlePublishedTime={article.date}
        articleModifiedTime={article.date}
        articleAuthor={article.author}
        articleTags={article.tags}
      />

      <div className="min-h-screen bg-background-50">
        <Header />
        <main className="pt-20 pb-10">
          <article className="w-full px-4 md:px-6">
            <div className="max-w-4xl mx-auto">
              <div className="mb-4">
                <Breadcrumb items={breadcrumbItems} />
              </div>

              <div className="mb-6">
                <div className="flex flex-wrap items-center gap-3 mb-4">
                  <Link
                    to={`/category/${article.category}`}
                    className="px-3 py-1 bg-primary-100 text-primary-700 text-xs font-medium rounded-full whitespace-nowrap cursor-pointer"
                  >
                    {categoryInfo?.name}
                  </Link>
                  {article.tags.map((tag) => (
                    <span
                      key={tag}
                      className="px-3 py-1 bg-background-200/70 text-foreground-600 text-xs rounded-full whitespace-nowrap"
                    >
                      #{tag}
                    </span>
                  ))}
                </div>

                <h1 className="text-2xl md:text-3xl lg:text-4xl font-bold text-foreground-950 font-heading leading-tight mb-4">
                  {article.title}
                </h1>

                <div className="flex flex-wrap items-center gap-4 text-sm text-foreground-500 mb-6">
                  <span className="flex items-center gap-1">
                    <i className="ri-user-line"></i>
                    {article.author}
                  </span>
                  <span className="flex items-center gap-1">
                    <i className="ri-calendar-line"></i>
                    {article.date}
                  </span>
                  <span className="flex items-center gap-1">
                    <i className="ri-eye-line"></i>
                    {article.views.toLocaleString()} 阅读
                  </span>
                </div>
              </div>

              <div className="mb-8 rounded-xl overflow-hidden">
                <img
                  src={article.image}
                  alt={article.title}
                  title={`${article.title} - 51网址导航`}
                  className="w-full h-auto object-cover max-h-[450px]"
                />
              </div>

              <div
                className="prose prose-sm md:prose-base max-w-none text-foreground-800 leading-relaxed"
                dangerouslySetInnerHTML={{ __html: article.content }}
              />

              <div className="mt-10 pt-8 border-t border-background-200/70">
                <div className="flex flex-wrap items-center gap-3">
                  <span className="text-sm text-foreground-500">标签：</span>
                  {article.tags.map((tag) => (
                    <span
                      key={tag}
                      className="px-3 py-1.5 bg-background-100 text-foreground-600 text-xs rounded-full border border-background-200/70 whitespace-nowrap"
                    >
                      #{tag}
                    </span>
                  ))}
                </div>
              </div>
            </div>
          </article>

          {relatedArticles.length > 0 && (
            <section className="w-full px-4 md:px-6 mt-12">
              <div className="max-w-4xl mx-auto">
                <div className="flex items-center gap-3 mb-5">
                  <div className="w-1 h-5 bg-accent-500 rounded-full"></div>
                  <h2 className="text-lg font-bold text-foreground-950 font-heading">
                    相关文章
                  </h2>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                  {relatedArticles.map((ra) => (
                    <Link
                      key={ra.id}
                      to={`/article/${ra.id}`}
                      className="group bg-background-50 rounded-xl border border-background-200/70 hover:border-primary-300 overflow-hidden transition-all cursor-pointer"
                    >
                      <div className="relative w-full h-36 overflow-hidden">
                        <img
                          src={ra.image}
                          alt={ra.title}
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                        />
                      </div>
                      <div className="p-4">
                        <h3 className="text-sm font-medium text-foreground-900 line-clamp-2 group-hover:text-primary-600 transition-colors leading-relaxed">
                          {ra.title}
                        </h3>
                        <p className="text-xs text-foreground-400 mt-2">{ra.date}</p>
                      </div>
                    </Link>
                  ))}
                </div>
              </div>
            </section>
          )}
        </main>
        <Footer />
      </div>
    </>
  );
}